import { useState } from 'react';
import {
  Check,
  CheckCheck,
  ChevronDown,
  ChevronUp,
  CircleAlert,
  CircleHelp,
  Clock,
  Database,
  TriangleAlert,
  Zap,
} from 'lucide-react';
import { Link } from '@tanstack/react-router';
import { Badge, Button, ChatMessageMeta, IconButton, Tooltip, cn } from '@wundo/ui';
import { formatMessageDateTime } from '@/utils/messageUtils.ts';
import type { Message } from '@/types/messages.ts';

/*
 * What happened to a message after it was written: whether it was delivered,
 * read, retried or dropped. One icon, because only one of them can be true.
 */
function DeliveryStatus({ message }: { message: Message }) {
  const retry = message.meta?.retry_status;

  if (retry === 'pending') {
    return (
      <Tooltip content='Delivery pending retry'>
        <Clock
          className='size-3.5 text-ink-secondary'
          aria-label='Delivery pending retry'
        />
      </Tooltip>
    );
  }
  if (retry === 'dropped') {
    return (
      <Tooltip content='Delivery dropped (user replied)'>
        <CircleAlert className='size-3.5 text-ink-muted' aria-label='Delivery dropped' />
      </Tooltip>
    );
  }
  if (retry === 'failed') {
    return (
      <Tooltip content='Delivery failed'>
        <TriangleAlert className='size-3.5 text-danger' aria-label='Delivery failed' />
      </Tooltip>
    );
  }
  if (message.read_at) {
    return (
      <Tooltip content='Read'>
        <CheckCheck className='size-3.5 text-ink' aria-label='Read' />
      </Tooltip>
    );
  }
  if (message.delivered_at) {
    return (
      <Tooltip content='Delivered'>
        <Check className='size-3.5 text-ink-muted' aria-label='Delivered' />
      </Tooltip>
    );
  }

  return null;
}

/* Whether the classifier has been over this message, and what it made of it. */
function Classification({ message }: { message: Message }) {
  const classification = message.classification;

  if (!classification) {
    return (
      <Tooltip content='Pending classification'>
        <Clock
          className='size-3.5 text-ink-secondary'
          aria-label='Pending classification'
        />
      </Tooltip>
    );
  }

  if (!classification.is_question) {
    return (
      <Tooltip content='Processed'>
        <Check className='size-3.5 text-ink-muted' aria-label='Processed' />
      </Tooltip>
    );
  }

  const category = classification.question_category?.replace(/_/g, ' ');

  return (
    <Badge tone='info' className='gap-1'>
      <CircleHelp className='size-3' aria-hidden='true' />
      {category ?? 'Question'}
    </Badge>
  );
}

/*
 * When the message was said, and whether it arrived. On a touch screen the time
 * is what asks for the tools; elsewhere a hover does.
 */
function MessageTime({
  message,
  toolsShown,
  onToggleTools,
}: {
  message: Message;
  /** Whether the tools are showing; undefined when the message has none. */
  toolsShown: boolean | undefined;
  onToggleTools: () => void;
}) {
  const when = (
    <>
      {formatMessageDateTime(message.created_at)}
      <DeliveryStatus message={message} />
    </>
  );

  if (toolsShown === undefined) {
    return <span className='flex items-center gap-1 tabular-nums'>{when}</span>;
  }

  return (
    <Button
      variant='link'
      size='sm'
      aria-expanded={toolsShown}
      title={toolsShown ? 'Hide message actions' : 'Show message actions'}
      className={cn(
        'gap-1 rounded-sm text-xs font-normal text-ink-muted tabular-nums no-underline hover:text-ink-muted',
        toolsShown && '-ml-1 bg-fill px-1',
      )}
      onClick={onToggleTools}
    >
      {when}
    </Button>
  );
}

/* The ways out of a message: the trace it ran in, the memories the agent was holding. */
function MessageTools({
  message,
  traceId,
  onOpenMemories,
}: {
  message: Message;
  traceId: string | undefined;
  onOpenMemories: ((offeredHandles: string[]) => void) | undefined;
}) {
  const handles = message.meta?.offered_memory_handles;
  const offered = Array.isArray(handles) ? handles : [];

  return (
    <>
      {traceId && (
        <Tooltip content='Trace'>
          <Link
            to='/traces/$traceId'
            params={{ traceId }}
            target='_blank'
            aria-label='Trace'
            className='inline-flex size-6 items-center justify-center rounded-full text-ink-secondary hover:bg-fill hover:text-ink'
          >
            <Zap className='size-3' aria-hidden='true' />
          </Link>
        </Tooltip>
      )}
      {onOpenMemories && (
        <Tooltip content="View this user's memories">
          <IconButton
            size='sm'
            className='size-6'
            aria-label="View this user's memories"
            onClick={() => onOpenMemories(offered)}
          >
            <Database className='size-3' aria-hidden='true' />
          </IconButton>
        </Tooltip>
      )}
    </>
  );
}

/* The language the message was written in and the one it was translated to; a tap swaps them. */
function TranslationToggle({
  from,
  to,
  showingOriginal,
  onToggle,
}: {
  from: string | undefined;
  to: string;
  showingOriginal: boolean;
  onToggle: () => void;
}) {
  return (
    <>
      <span aria-hidden='true'>·</span>
      <Button
        variant='link'
        size='sm'
        className='text-xs font-normal text-ink-muted'
        onClick={onToggle}
        title={`Click to ${showingOriginal ? 'view translated' : 'view original'} message`}
      >
        {from} › {to}
      </Button>
    </>
  );
}

function ReasoningToggle({
  open,
  onToggle,
}: {
  open: boolean | undefined;
  onToggle: () => void;
}) {
  return (
    <>
      <span aria-hidden='true'>·</span>
      <Button
        variant='link'
        size='sm'
        aria-expanded={open}
        className='gap-0.5 text-xs font-semibold text-ink-secondary no-underline hover:underline'
        onClick={onToggle}
      >
        Reasoning
        {open ? (
          <ChevronUp className='size-3' aria-hidden='true' />
        ) : (
          <ChevronDown className='size-3' aria-hidden='true' />
        )}
      </Button>
    </>
  );
}

export interface MessageFooterProps {
  message: Message;
  /** Right, under the user's own words; left, under everything else. */
  fromUser: boolean;
  /** Shows the classification pills. Debug mode turns them on for every message. */
  showClassifications: boolean;
  /** Whether the original-language body is being shown instead of the translation. */
  showingOriginal: boolean;
  onToggleOriginal: () => void;
  /** Whether the agent's reasoning is unfolded under this line; left out when it has none. */
  reasoningOpen?: boolean;
  onToggleReasoning?: () => void;
  /** Opens this user's memories with the handles this turn was offered. */
  onOpenMemories?: (offeredHandles: string[]) => void;
  /** Under a full-width row such as a tool call, which has no avatar to line up with. */
  flush?: boolean;
}

/**
 * The line under a message: when it was said and what became of it. The ways
 * out of it — the trace it ran in, the memories the agent was holding — wait
 * for a hover, a focus, or a tap on the time.
 */
export function MessageFooter({
  message,
  fromUser,
  showClassifications,
  showingOriginal,
  onToggleOriginal,
  reasoningOpen,
  onToggleReasoning,
  onOpenMemories,
  flush = false,
}: MessageFooterProps) {
  const [toolsShown, setToolsShown] = useState(false);

  const original = message.meta?.original_language;
  const translated =
    message.body.type !== 'button' &&
    message.body.type !== 'attachment' &&
    Boolean(original) &&
    original !== message.language;

  const traceId = message.meta?.trace_id;
  const memories = Boolean(onOpenMemories) && !fromUser;
  const tools =
    traceId || memories ? (
      <MessageTools
        message={message}
        traceId={traceId}
        onOpenMemories={memories ? onOpenMemories : undefined}
      />
    ) : null;

  return (
    <ChatMessageMeta
      author={fromUser ? 'user' : 'wundo'}
      tools={tools}
      toolsShown={toolsShown}
      className={cn(flush && 'pl-0')}
    >
      {showClassifications && fromUser && <Classification message={message} />}

      <MessageTime
        message={message}
        toolsShown={tools ? toolsShown : undefined}
        onToggleTools={() => setToolsShown((shown) => !shown)}
      />

      {translated && (
        <TranslationToggle
          from={original}
          to={message.language}
          showingOriginal={showingOriginal}
          onToggle={onToggleOriginal}
        />
      )}

      {onToggleReasoning && (
        <ReasoningToggle open={reasoningOpen} onToggle={onToggleReasoning} />
      )}
    </ChatMessageMeta>
  );
}
